import React, { useState, useEffect } from 'react';
import {
  LogOut, Search, User, MapPin, DollarSign, Clock,
  ChevronRight, ArrowLeft, Edit, Send, CheckCircle,
  Info, X, FileText, Download, Briefcase, Users, Database, Menu, Wrench, PlusCircle
} from 'lucide-react';
import axios from 'axios';
import './RecruiterStyles.css';
import RecruiterCandidateSubmissionForm from './RecruiterCandidateSubmissionForm';
import RecruiterSubmissionsDatabase    from './RecruiterSubmissionsDatabase';
import BeelineSubmissionForm           from '../Vendorfiles/BeelineSubmissionForm';
import PostedJobs                      from '../Adminfiles/PostedJobs';
import ResumeWatermark                 from '../Adminfiles/ResumeWatermark';
import ExistingCandidatesModal          from '../Adminfiles/ExistingCandidatesModal';
import JobPosting from '../Adminfiles/JobPosting';

export default function RecruiterDashboard({ onLogout }) {
  const [activeTab, setActiveTab]         = useState('browse-jobs');
  const [jobFilter, setJobFilter]         = useState('open');
  const [viewState, setViewState]         = useState('LIST');
  const [showBeeline, setShowBeeline]     = useState(false);
  const [selectedJob, setSelectedJob]     = useState(null);
  const [showModal, setShowModal]         = useState(false);
  const [profileData, setProfileData]     = useState(null);
  const [mockJobs, setMockJobs]           = useState([]);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [submittedCandidates, setSubmittedCandidates] = useState([]);
  const [loadingCandidates, setLoadingCandidates]     = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showExisting, setShowExisting] = useState(false);
const [totalSubmitted, setTotalSubmitted] = useState(null);
  const [prefillData, setPrefillData] = useState(null);

  useEffect(() => {
    if (profileData?._id) fetchJobs(profileData._id);
  }, [profileData]);

  useEffect(() => { fetchProfileData(); }, []);

useEffect(() => {
  if (!showModal || !selectedJob) return;
  let cancelled = false;
  setTotalSubmitted(null);

  axios
    .get(`${process.env.REACT_APP_API_BASE_URL}/api/recruiters/jobs/${selectedJob._id}/submission-count`)
    .then(({ data }) => { if (!cancelled) setTotalSubmitted(data.total); })
    .catch(() => { if (!cancelled) setTotalSubmitted(0); });

  return () => { cancelled = true; };
}, [showModal, selectedJob]);

  const fetchProfileData = async () => {
    try {
      const stored = localStorage.getItem('recruiterProfileData');
      if (stored) {
        const parsed = JSON.parse(stored);
        setProfileData(parsed);
        setLoadingProfile(false);
        try {
          const { data } = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/api/recruiters/${parsed._id}`
          );
          setProfileData(data);
          localStorage.setItem('recruiterProfileData', JSON.stringify(data));
        } catch {}
      } else {
        setLoadingProfile(false);
      }
    } catch { setLoadingProfile(false); }
  };

  const fetchJobs = async (partnerId) => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/jobs`,
        { params: { partnerId } }
      );
      setMockJobs(data);
    } catch (e) { console.error(e); }
  };

  const isJobOpen = (job) => {
    if (!job.applyByDate) return true;
    const d = new Date(job.applyByDate);
    if (job.applyByTime) {
      const [h, m] = job.applyByTime.split(':');
      d.setHours(+h || 23, +m || 59, 59, 999);
    } else d.setHours(23, 59, 59, 999);
    return new Date() <= d;
  };

  const filteredJobs = mockJobs.filter(job => {
  const isOpen    = isJobOpen(job);
  const statusMatch = jobFilter === 'open' ? isOpen : !isOpen;
  const typeMatch   = showBeeline ? job.isBeeline === true : !job.isBeeline;

  const q = searchQuery.trim().toLowerCase();
  const searchMatch = !q || [
    job.role,
    job.companyName,
    job.location,
    job.beelineId,
    job.requestId,
  ].some(v => v && String(v).toLowerCase().includes(q));

  return statusMatch && typeMatch && searchMatch;
});

  const standardCount = mockJobs.filter(j => !j.isBeeline && isJobOpen(j)).length;
  const beelineCount  = mockJobs.filter(j => j.isBeeline && isJobOpen(j)).length;

  const fmt = (ds) => {
    if (!ds) return '--';
    return ds.includes('T') ? ds.split('T')[0] : ds;
  };

  const getFreshFileUrl = (url) => {
  if (!url) return '#';
  return `${process.env.REACT_APP_API_BASE_URL}/api/candidates/view-file?url=${encodeURIComponent(url)}`;
};

  const handleJobClick       = (job) => { setSelectedJob(job); setShowModal(true); };
  const handleBack = () => { setViewState('LIST'); setSelectedJob(null); setPrefillData(null); };
  const handleStartSubmission = () => {
    setPrefillData(null);
    setShowModal(false);
    setViewState(selectedJob?.isBeeline ? 'SUBMIT_BEELINE' : 'SUBMIT_CANDIDATE');
  };

  const handleViewSubmissions = async () => {
    if (!selectedJob || !profileData) return;
    setLoadingCandidates(true);
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/recruiters/${profileData._id}/jobs/${selectedJob._id}/candidates`
      );
      setSubmittedCandidates(data);
      setShowModal(false);
      setViewState('VIEW_SUBMISSIONS');
    } catch { alert('Failed to load submissions.'); }
    finally { setLoadingCandidates(false); }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    const updates = Object.fromEntries(new FormData(e.target).entries());
    try {
      const { data } = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/recruiters/${profileData._id}`, updates
      );
      setProfileData(data);
      localStorage.setItem('recruiterProfileData', JSON.stringify(data));
      alert('Profile updated!');
      setViewState('LIST');
    } catch { alert('Update failed.'); }
  };

  /* ════════════════════════════════
     RENDERERS
  ════════════════════════════════ */
  const renderJobsList = () => (
    <>
      <div className="r-tabs-header">
        {/* Tab group */}
        <div className="r-tab-group">
          <button
            className={`r-tab-btn ${jobFilter === 'open' ? 'active' : ''}`}
            onClick={() => setJobFilter('open')}
          >Open Jobs</button>
          <button
            className={`r-tab-btn ${jobFilter === 'closed' ? 'active' : ''}`}
            onClick={() => setJobFilter('closed')}
          >Closed Jobs</button>
        </div>

        {/* Search */}
        <div className="r-search-box">
          <Search size={15} className="r-search-icon" />
          <input
            type="text"
            placeholder="Search by title, company, location or ID…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="r-search-clear" onClick={() => setSearchQuery('')}>
              <X size={14} />
            </button>
          )}
        </div>

        {/* Toggle */}
        <div className="r-toggle-wrapper" onClick={() => setShowBeeline(b => !b)}>
          <span className={`r-toggle-label ${!showBeeline ? 'active' : ''}`}>
            Standard
            {standardCount > 0 && <span className="r-toggle-count">{standardCount}</span>}
          </span>

          <div className={`r-toggle-track ${showBeeline ? 'on' : 'off'}`}>
            <div className="r-toggle-thumb" />
          </div>

          <span className={`r-toggle-label ${showBeeline ? 'active beeline' : ''}`}>
            Beeline
            {beelineCount > 0 && (
              <span className={`r-toggle-count ${showBeeline ? 'beeline' : ''}`}>
                {beelineCount}
              </span>
            )}
          </span>
        </div>
      </div>

      <div className="r-jobs-grid">
        {filteredJobs.length > 0 ? filteredJobs.map(job => (
          <div key={job._id} className="r-job-card" onClick={() => handleJobClick(job)}>
            <div className="r-job-header">
              <div className="r-company-icon">{job.companyName?.[0] || 'C'}</div>
              <div className="r-job-info">
                <h3>{job.role}</h3>
                <p>{job.companyName}</p>
                {job.isBeeline && (
                  <div style={{ marginTop: '5px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                    {job.assignedTo?.includes(profileData?._id) ? (
                      <span style={{ fontSize:'0.68rem', background:'#eff6ff', color:'#2563eb', padding:'2px 7px', borderRadius:'4px', border:'1px solid #bfdbfe', fontWeight:'700' }}>
                        Assigned to You
                      </span>
                    ) : (
                      <span style={{ fontSize:'0.68rem', background:'#f0f9ff', color:'#0369a1', padding:'2px 7px', borderRadius:'4px', border:'1px solid #bae6fd', fontWeight:'700' }}>
                        Open Beeline
                      </span>
                    )}
                    {(job.beelineId || job.requestId) && (
                      <span style={{ fontSize:'0.68rem', background:'#f8fafc', color:'#475569', padding:'2px 7px', borderRadius:'4px', border:'1px solid #e2e8f0', fontWeight:'600' }}>
                        ID: {job.beelineId || job.requestId}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <ChevronRight className="r-arrow-icon" size={18} />
            </div>
            <div className="r-job-divider" />
            <div className="r-job-tags">
              <span><MapPin size={12} /> {job.location}</span>
              <span><DollarSign size={12} /> {job.ctc}</span>
              <span><Clock size={12} /> {fmt(job.applyByDate)}{job.applyByTime ? ` ${job.applyByTime}` : ''}</span>
            </div>
          </div>
        )) : (
          <div className="r-empty-state">
            <Search size={36} color="#c4b5fd" />
            <p>
  {searchQuery
    ? `No ${jobFilter} ${showBeeline ? 'Beeline requests' : 'standard jobs'} match "${searchQuery}".`
    : `No ${jobFilter} ${showBeeline ? 'Beeline requests' : 'standard jobs'} found.`}
</p>
          </div>
        )}
      </div>
    </>
  );

  const renderProfile = () => {
    if (!profileData) return <div className="r-empty-state">Profile unavailable.</div>;

    if (viewState === 'EDIT_PROFILE') return (
      <div className="r-profile-edit-container">
        <button className="r-back-btn" onClick={() => { setViewState('LIST'); setActiveTab('profile'); }}>
          <ArrowLeft size={14} /> Cancel
        </button>
        <div className="r-form-card">
          <div className="r-form-card-header"><h3>Edit Agency Profile</h3></div>
          <div className="r-form-card-body">
            <form onSubmit={handleProfileUpdate}>
              <div className="r-form-group">
                <label>Agency / Name</label>
                <input name="recruiterName" defaultValue={profileData.recruiterName} required />
              </div>
              <div className="r-form-group">
                <label>Email</label>
                <input name="email" defaultValue={profileData.email} disabled className="disabled-input" />
              </div>
              <div className="r-form-group">
                <label>Phone</label>
                <input name="phone" defaultValue={profileData.phone} />
              </div>
              <div className="r-form-group">
                <label>Address</label>
                <input name="address" defaultValue={profileData.address} />
              </div>
              <button type="submit" className="r-submit-btn">Save Changes</button>
            </form>
          </div>
        </div>
      </div>
    );

    return (
      <div className="r-profile-view">
        <div className="r-profile-header-card">
          <div className="r-profile-avatar">{profileData.recruiterName?.charAt(0) || 'R'}</div>
          <div>
            <h1>{profileData.recruiterName || 'Recruiter'}</h1>
            <p>ID: {profileData.recruiterId || profileData._id}</p>
          </div>
          <button className="r-edit-profile-btn" onClick={() => setViewState('EDIT_PROFILE')}>
            <Edit size={14} /> Edit Profile
          </button>
        </div>
        <div className="r-profile-details-grid">
          <div className="r-detail-box">
            <h4>Contact Info</h4>
            <p>{profileData.email}</p>
            <p>{profileData.phone || '--'}</p>
          </div>
          <div className="r-detail-box">
            <h4>Address</h4>
            <p>{profileData.address || '--'}</p>
          </div>
        </div>
      </div>
    );
  };

  const renderSubmissionsList = () => (
    <div className="r-preview-container">
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'20px' }}>
        <button className="r-back-btn" onClick={handleBack} style={{ margin:0 }}>
          <ArrowLeft size={14} /> Back to Jobs
        </button>
        <div style={{ textAlign:'right' }}>
          <h2 style={{ margin:0, fontSize:'1.1rem', color:'#0f172a' }}>{selectedJob?.role}</h2>
          <p style={{ margin:0, color:'#64748b', fontSize:'0.8rem' }}>Your Submissions</p>
        </div>
      </div>

      <div style={{ background:'white', borderRadius:'12px', border:'1px solid #e2e8f0', overflow:'hidden' }}>
        {loadingCandidates ? (
          <div style={{ padding:'40px', textAlign:'center', color:'#64748b' }}>Loading…</div>
        ) : (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.82rem', whiteSpace:'nowrap' }}>
              <thead>
                <tr style={{ background:'#f8f7ff', borderBottom:'2px solid #e2e8f0', textAlign:'left', color:'#7c3aed', fontSize:'0.68rem', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.5px' }}>
                  {['Job Role','Name','Email','Phone','Total Exp','Rel Exp','Current Org','Current CTC','Exp CTC','Working?','Notice','Education','Skills','Status','Resume']
                    .map(h => <th key={h} style={{ padding:'11px 13px' }}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {submittedCandidates.length > 0 ? submittedCandidates.map(c => (
                  <tr key={c._id} style={{ borderBottom:'1px solid #f1f5f9' }}>
                    <td style={{ padding:'10px 13px', color:'#7c3aed', fontWeight:'600' }}>{selectedJob?.role}</td>
                    <td style={{ padding:'10px 13px', fontWeight:'700', color:'#0f172a' }}>{c.candidateName}</td>
                    <td style={{ padding:'10px 13px', color:'#3b82f6' }}>{c.candidateEmail}</td>
                    <td style={{ padding:'10px 13px' }}>{c.contactNumber || '--'}</td>
                    <td style={{ padding:'10px 13px' }}>{c.totalExperience || '--'}</td>
                    <td style={{ padding:'10px 13px' }}>{c.relevantExperience || '--'}</td>
                    <td style={{ padding:'10px 13px' }}>{c.currentCompanyName || '--'}</td>
                    <td style={{ padding:'10px 13px' }}>{c.currentCTC || '--'}</td>
                    <td style={{ padding:'10px 13px' }}>{c.expectedCTC || '--'}</td>
                    <td style={{ padding:'10px 13px' }}>{c.currentlyWorking || '--'}</td>
                    <td style={{ padding:'10px 13px', color:'#d97706', fontWeight:'500' }}>{c.noticePeriod || '--'}</td>
                    <td style={{ padding:'10px 13px', whiteSpace:'normal', maxWidth:'160px', fontSize:'0.75rem' }}>{c.education || '--'}</td>
                    <td style={{ padding:'10px 13px', whiteSpace:'normal', maxWidth:'180px', fontSize:'0.75rem', color:'#475569' }}>{c.skillSet || '--'}</td>
                    <td style={{ padding:'10px 13px' }}>
                      <span style={{
                        padding:'2px 9px', borderRadius:'20px', fontSize:'0.65rem', fontWeight:'700', textTransform:'uppercase',
                        background: c.candidateStatus?.includes('Select') ? '#dcfce7' : c.candidateStatus?.includes('Reject') ? '#fee2e2' : '#fef9c3',
                        color:      c.candidateStatus?.includes('Select') ? '#15803d' : c.candidateStatus?.includes('Reject') ? '#991b1b' : '#854d0e',
                        border:     c.candidateStatus?.includes('Select') ? '1px solid #bbf7d0' : c.candidateStatus?.includes('Reject') ? '1px solid #fecaca' : '1px solid #fde68a',
                      }}>
                        {c.candidateStatus || 'Pending'}
                      </span>
                    </td>
                    <td style={{ padding:'10px 13px' }}>
                      {c.resumeFile?.fileUrl ? (
                        <a
                          href={getFreshFileUrl(c.resumeFile.fileUrl)}
                          target="_blank" rel="noreferrer"
                          style={{ color:'#7c3aed', textDecoration:'none', display:'flex', alignItems:'center', gap:'4px', fontSize:'0.75rem', fontWeight:'600', padding:'3px 8px', background:'#f5f3ff', border:'1px solid #ddd6fe', borderRadius:'5px' }}
                        >
                          <FileText size={11} /> PDF
                        </a>
                      ) : '--'}
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={15} style={{ padding:'50px', textAlign:'center', color:'#94a3b8' }}>
                      No submissions for this job yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  /* ════════════════════════════════
     JOB DETAILS MODAL
  ════════════════════════════════ */
  const JobDetailsModal = () => {
    if (!showModal || !selectedJob) return null;
    const open = isJobOpen(selectedJob);
    const fileUrl = selectedJob.fileUrl || selectedJob.localJdFile;
    const finalLink = fileUrl ? getFreshFileUrl(fileUrl) : '#';

    return (
      <div className="modal-overlay" onClick={() => setShowModal(false)}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>

          <div className="modal-header">
            <div>
              <h2>{selectedJob.role}</h2>
              <p>{selectedJob.companyName} · {selectedJob.location} · {selectedJob.typeOfWork}</p>
              {selectedJob.isBeeline && (
                <div style={{ marginTop:'8px', display:'flex', gap:'8px', flexWrap:'wrap' }}>
                  {selectedJob.assignedTo?.includes(profileData?._id) ? (
                    <span style={{ fontSize:'0.75rem', background:'#eff6ff', color:'#2563eb', padding:'3px 8px', borderRadius:'5px', border:'1px solid #bfdbfe', fontWeight:'700' }}>
                      Assigned to You
                    </span>
                  ) : (
                    <span style={{ fontSize:'0.75rem', background:'#f0f9ff', color:'#0369a1', padding:'3px 8px', borderRadius:'5px', border:'1px solid #bae6fd', fontWeight:'700' }}>
                      Open Beeline Request
                    </span>
                  )}
                  {selectedJob.requestId && <span style={{ fontSize:'0.75rem', color:'#64748b' }}>Req: {selectedJob.requestId}</span>}
                  {selectedJob.beelineId && <span style={{ fontSize:'0.75rem', color:'#64748b' }}>BL: {selectedJob.beelineId}</span>}
                </div>
              )}
            </div>
            <button className="close-btn" onClick={() => setShowModal(false)}><X size={18} /></button>
          </div>

          <div className="modal-body">
            <div className="detail-section">
              <div className="info-grid-3">
                <div className="info-item"><label>CTC / Stipend</label><span style={{ color:'#059669' }}>{selectedJob.ctc}</span></div>
                <div className="info-item"><label>Batch</label><span>{selectedJob.batch || 'Any'}</span></div>
                <div className="info-item"><label>Apply By</label><span style={{ color:'#dc2626' }}>{fmt(selectedJob.applyByDate)}{selectedJob.applyByTime ? ` ${selectedJob.applyByTime}` : ''}</span></div>
<div className="info-item">
  <label>Total Submitted</label>
  <span style={{ color:'#7c3aed' }}>{totalSubmitted === null ? '…' : totalSubmitted}</span>
</div>
              </div>
            </div>

            <div className="detail-section">
              <h4><CheckCircle size={12} /> Eligibility</h4>
              <div className="info-grid-3" style={{ rowGap:'16px' }}>
                <div className="info-item"><label>10th %</label><span>{selectedJob.tenth || '--'}%</span></div>
                <div className="info-item"><label>12th %</label><span>{selectedJob.twelfth || '--'}%</span></div>
                <div className="info-item"><label>Min CGPA</label><span>{selectedJob.minCGPA || '--'}</span></div>
                <div className="info-item"><label>Passout Year</label><span>{selectedJob.passoutYear || 'Any'}</span></div>
                <div className="info-item"><label>Gender</label><span>{selectedJob.gender}</span></div>
                <div className="info-item"><label>Backlogs</label><span>{selectedJob.maxHistoryArrears}/{selectedJob.maxCurrentArrears}</span></div>
              </div>
            </div>

            {selectedJob.aboutJob && (
              <div className="detail-section">
                <h4>About the Job</h4>
                <div className="text-block">{selectedJob.aboutJob}</div>
              </div>
            )}

            {selectedJob.aboutCompany && (
              <div className="detail-section">
                <h4>About Company</h4>
                <div className="text-block">{selectedJob.aboutCompany}</div>
              </div>
            )}

            {selectedJob.hiringWorkflows && (
              <div className="detail-section">
                <h4>Hiring Workflow</h4>
                <div className="text-block">{selectedJob.hiringWorkflows}</div>
              </div>
            )}

            {fileUrl && (
              <div className="detail-section">
                <h4><FileText size={11} /> Attached JD</h4>
                <div style={{ background:'#f8f7ff', padding:'12px', borderRadius:'8px', display:'flex', alignItems:'center', justifyContent:'space-between', border:'1px solid #ddd6fe' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                    <FileText size={18} color="#7c3aed" />
                    <div>
                      <div style={{ fontSize:'0.88rem', color:'#334155', fontWeight:'600' }}>Job Description</div>
                      <div style={{ fontSize:'0.72rem', color:'#94a3b8' }}>Click to view / download</div>
                    </div>
                  </div>
                  <a href={finalLink} target="_blank" rel="noreferrer"
                    style={{ textDecoration:'none', color:'#7c3aed', display:'flex', alignItems:'center', gap:'5px', fontSize:'0.82rem', fontWeight:'600', background:'white', padding:'6px 12px', borderRadius:'6px', border:'1px solid #ddd6fe' }}>
                    <Download size={14} /> Download
                  </a>
                </div>
              </div>
            )}

            {(selectedJob.externalLink || selectedJob.additionalInfo) && (
              <div className="detail-section">
                <h4>Additional Info</h4>
                {selectedJob.externalLink && (
                  <a href={selectedJob.externalLink} target="_blank" rel="noreferrer" className="external-link-box">
                    <Info size={16} /> {selectedJob.externalLink}
                  </a>
                )}
                {selectedJob.additionalInfo && (
                  <p style={{ marginTop:'10px', fontSize:'0.85rem', color:'#64748b', fontStyle:'italic' }}>
                    Note: {selectedJob.additionalInfo}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="modal-footer">
                      {open ? (
            <>
              {selectedJob.isBeeline && (
                <button className="r-modal-secondary-btn" onClick={() => setShowExisting(true)}>
                  <Database size={15} /> Existing Candidates
                </button>
              )}
              <button className="r-modal-secondary-btn" onClick={handleViewSubmissions}>
                <Users size={15} /> View Submitted
              </button>
                <button className="r-modal-submit-btn" onClick={handleStartSubmission}>
                  <Send size={15} />
                  {selectedJob.isBeeline ? 'Submit to Beeline' : 'Submit Candidate'}
                </button>
              </>
            ) : (
              <span style={{ color:'#ef4444', fontWeight:'600', fontSize:'0.85rem' }}>
                Applications are closed.
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loadingProfile) return <div className="r-page-loading">Loading Recruiter Portal…</div>;

  /* ════════════════════════════════
     MAIN RENDER
  ════════════════════════════════ */
  return (
    <div className="recruiter-dashboard-container">

      {/* ── Sidebar ── */}
      <aside className={`r-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <button className="r-sidebar-close" onClick={() => setSidebarOpen(false)}>
          <X size={18} />
        </button>
        <div className="r-sidebar-header">
          <div className="r-logo-icon"><Briefcase size={18} color="white" /></div>
          <h2>Recruiter Portal</h2>
        </div>

        <nav className="r-sidebar-nav">
          <button
            className={`r-nav-item ${activeTab === 'browse-jobs' ? 'active' : ''}`}
            onClick={() => { setActiveTab('browse-jobs'); setViewState('LIST'); setSidebarOpen(false); }}
          >
            <Search size={18} /><span>Job Board</span>
          </button>
          <button
            className={`r-nav-item ${activeTab === 'all-submissions' ? 'active' : ''}`}
            onClick={() => { setActiveTab('all-submissions'); setViewState('LIST'); setSidebarOpen(false); }}
          >
            <Database size={18} /><span>Candidates Applied</span>
          </button>
          <button
            className={`r-nav-item ${activeTab === 'manage-candidates' ? 'active' : ''}`}
            onClick={() => { setActiveTab('manage-candidates'); setViewState('LIST'); setSidebarOpen(false); }}
          >
            <Briefcase size={18} /><span>Manage Candidates</span>
          </button>
          <button
  className={`r-nav-item ${activeTab === 'post-job' ? 'active' : ''}`}
  onClick={() => { setActiveTab('post-job'); setViewState('LIST'); setSidebarOpen(false); }}
>
  <PlusCircle size={18} /><span>Post a Job</span>
</button>
          <button
            className={`r-nav-item ${activeTab === 'resume-watermark' ? 'active' : ''}`}
            onClick={() => { setActiveTab('resume-watermark'); setViewState('LIST'); setSidebarOpen(false); }}
          >
            <Wrench size={18} /><span>Resume Watermark</span>
          </button>
          <button
            className={`r-nav-item ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => { setActiveTab('profile'); setViewState('LIST'); setSidebarOpen(false); }}
          >
            <User size={18} /><span>Agency Profile</span>
          </button>
        </nav>

        <div className="r-sidebar-footer">
          <button className="r-nav-item r-logout-btn" onClick={onLogout}>
            <LogOut size={18} /><span>Log Out</span>
          </button>
        </div>
      </aside>

      {/* ── Main ── */}
      <main className="r-main-content">

        {/* Top Bar */}
        <div className="r-top-bar">
          <button className="r-hamburger-btn" onClick={() => setSidebarOpen(true)}>
            <Menu size={20} />
          </button>
          <h2>
            {activeTab === 'browse-jobs'        ? 'Job Board'
            : activeTab === 'all-submissions'   ? 'Submissions Database'
            : activeTab === 'manage-candidates' ? 'Manage Candidates'
            : activeTab === 'resume-watermark'  ? 'Resume Watermark'
            : activeTab === 'post-job'          ? 'Post a Job'  
            : 'Agency Profile'}
          </h2>

          <div className="r-user-pill" onClick={() => { setActiveTab('profile'); setViewState('LIST'); }}>
            <div className="r-avatar">{profileData?.recruiterName?.charAt(0) || 'R'}</div>
            <span>{profileData?.recruiterName || 'Me'}</span>
          </div>
        </div>

        {/* Content */}
        <div className={`r-content-wrapper ${(activeTab === 'all-submissions' || activeTab === 'manage-candidates') ? 'submissions-mode' : ''}`}>

          {activeTab === 'browse-jobs' && viewState === 'LIST'             && renderJobsList()}
          {activeTab === 'browse-jobs' && viewState === 'SUBMIT_CANDIDATE' && (
            <RecruiterCandidateSubmissionForm
              job={selectedJob} recruiter={profileData} onBack={handleBack}
            />
          )}
          {activeTab === 'browse-jobs' && viewState === 'SUBMIT_BEELINE'   && (
            <BeelineSubmissionForm
              job={selectedJob} partner={profileData} prefillData={prefillData} onBack={handleBack}
            />
          )}
          {activeTab === 'browse-jobs' && viewState === 'VIEW_SUBMISSIONS' && renderSubmissionsList()}

          {activeTab === 'all-submissions' && (
            <RecruiterSubmissionsDatabase recruiterId={profileData?._id} />
          )}

          {activeTab === 'manage-candidates' && (
            <PostedJobs partnerId={profileData?._id} />
          )}
          {activeTab === 'resume-watermark' && <ResumeWatermark />}
          {activeTab === 'profile' && renderProfile()}
          {activeTab === 'post-job' && (
          <JobPosting
            isRecruiter
            onPost={() => {
              if (profileData?._id) fetchJobs(profileData._id);
              setActiveTab('browse-jobs');
            }}
          />
        )}
        </div>
      </main>

      {sidebarOpen && <div className="r-sidebar-overlay" onClick={() => setSidebarOpen(false)} />}
      <JobDetailsModal />

{showExisting && selectedJob && (
  <ExistingCandidatesModal
    job={selectedJob}
    // partnerId={profileData?._id} uncomment if needed to show only that recruiter's candidates
    onSelectCandidate={(p) => {
      setPrefillData(p);
      setShowExisting(false);
      setShowModal(false);
      setViewState('SUBMIT_BEELINE');
    }}
    onClose={() => setShowExisting(false)}
  />
)}
    </div>
  );
}